import React, { createContext, useCallback, useContext } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ProjectsAPI } from '@makyoapp/makyo-sdk'
import { fromSdkApp, ProjectAdapter } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { useParamId } from '@/hooks/useParamId'
import { projectPlanColor } from '@/shared/misc'

interface SingleProjectState {
  project?: ProjectAdapter
  isLoading: boolean
  updateProject: (cb: ProjectAdapter | ((project: ProjectAdapter) => void)) => void
}

const initialState: SingleProjectState = {
  project: undefined,
  isLoading: false,
  updateProject: () => {},
}

const SingleProjectContext = createContext<SingleProjectState>(initialState)

/**
 * useSingleProjectContext
 */
export function useSingleProjectContext() {
  return useContext(SingleProjectContext)
}

/**
 * SingleProjectProvider
 */
export function SingleProjectProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const projectId = useParamId('project')

  const { data: project, isLoading } = useQuery({
    queryKey: [queryKeys.projectDetail, projectId],
    queryFn: () => {
      if (!projectId) throw new Error('Project ID is required')
      return ProjectsAPI.ReqGet(projectId)
    },
    enabled: !!projectId,
    select: (res) => {
      return fromSdkApp(res, projectPlanColor)
    },
  })

  const update = useCallback(
    (cb: ProjectAdapter | ((project: ProjectAdapter) => void)) => {
      // we still need to revalidate query (to update the cache)
      queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, projectId] })
    },
    [projectId, queryClient]
  )

  return (
    <SingleProjectContext.Provider
      value={{
        project,
        isLoading,
        updateProject: update,
      }}
    >
      {children}
    </SingleProjectContext.Provider>
  )
}
